import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, BookOpen, Flame, Clock, CalendarCheck } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t, setLang } = useI18n();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [saadhnas, setSaadhnas] = useState([]);
  const [openSaadhna, setOpenSaadhna] = useState(null);

  useEffect(() => { if (user) setForm(user); }, [user]);
  useEffect(() => {
    if (user) api.get("/saadhnas/my").then(({ data }) => setSaadhnas(data)).catch(() => setSaadhnas([]));
  }, [user]);

  if (!user) return <Navigate to="/" replace />;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", form);
      setUser(data);
      if (data.language) setLang(data.language);
      toast.success("Profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const requestCore = async () => {
    setRequesting(true);
    try {
      await api.post("/auth/request-core-member");
      toast.success("Request sent to admin");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRequesting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif text-4xl text-[#5C061C]">{t("profile.title")}</h1>
        <div className="flex gap-2 items-center">
          <Badge className="bg-[#5C061C]/10 text-[#5C061C]">{t(`roles.${user.role}`)}</Badge>
          {user.is_approved_member && <Badge className="bg-[#15803D]/10 text-[#15803D]">✓ Approved Member</Badge>}
        </div>
      </div>

      <div className="mt-8 card-warm rounded-2xl p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("profile.name")}><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" className="bg-white border-[#E8DEC8]" /></Field>
          <Field label="Mobile"><Input value={user.mobile} disabled className="bg-[#F4EEDF] border-[#E8DEC8]" /></Field>
          <Field label={t("profile.dob")}><Input type="date" value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="profile-dob" className="bg-white border-[#E8DEC8]" /></Field>
          <Field label={t("profile.time_of_birth")}><Input type="time" value={form.time_of_birth || ""} onChange={(e) => setForm({ ...form, time_of_birth: e.target.value })} data-testid="profile-tob" className="bg-white border-[#E8DEC8]" /></Field>
          <Field label={t("profile.place_of_birth")}><Input value={form.place_of_birth || ""} onChange={(e) => setForm({ ...form, place_of_birth: e.target.value })} data-testid="profile-pob" className="bg-white border-[#E8DEC8]" /></Field>
          <Field label={t("profile.year_joined")}><Input type="number" value={form.year_joined || ""} onChange={(e) => setForm({ ...form, year_joined: Number(e.target.value) })} data-testid="profile-year" className="bg-white border-[#E8DEC8]" /></Field>
          <Field label={t("profile.joined_event")}><Input value={form.joined_via_event || ""} onChange={(e) => setForm({ ...form, joined_via_event: e.target.value })} data-testid="profile-event" className="bg-white border-[#E8DEC8]" /></Field>
          <Field label={t("profile.whatsapp")}><Input value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="profile-whatsapp" className="bg-white border-[#E8DEC8]" /></Field>
          <Field label={t("profile.email")}><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="profile-email" className="bg-white border-[#E8DEC8]" /></Field>
          <Field label={t("profile.language")}>
            <Select value={form.language || "en"} onValueChange={(v) => setForm({ ...form, language: v })}>
              <SelectTrigger data-testid="profile-language" className="bg-white border-[#E8DEC8]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="hi">हिन्दी</SelectItem>
                <SelectItem value="mr">मराठी</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label={t("profile.address")}><Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="profile-address" className="bg-white border-[#E8DEC8]" /></Field>

        <div className="pt-2 flex flex-wrap gap-3">
          <Button onClick={save} disabled={saving} className="btn-saffron rounded-full h-11 px-6" data-testid="profile-save-button">{saving ? "Saving..." : t("profile.save")}</Button>
          {!user.is_approved_member && user.role === "member" && (
            <Button onClick={requestCore} disabled={requesting} variant="outline" className="border-[#5C061C] text-[#5C061C] rounded-full h-11 px-6" data-testid="request-core-button">
              <Sparkles className="h-4 w-4 mr-2" />{t("profile.request_core")}
            </Button>
          )}
        </div>
      </div>

      {/* My Saadhnas */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-5 w-5 text-[#5C061C]" />
          <h2 className="font-serif text-2xl text-[#5C061C]">{t("saadhna.my_title")}</h2>
          <Badge className="bg-[#5C061C]/10 text-[#5C061C]">{saadhnas.length}</Badge>
        </div>
        {saadhnas.length === 0 ? (
          <div className="card-warm rounded-2xl p-8 text-center text-[#6E6259]">{t("saadhna.my_empty")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {saadhnas.map((e) => {
              const s = e.saadhna || {};
              const total = e.days || 1;
              const created = new Date(e.created_at);
              const now = new Date();
              const elapsedDays = Math.max(1, Math.min(total, Math.floor((now - created) / 86400000) + 1));
              const remaining = Math.max(0, total - elapsedDays + 1);
              const pct = Math.min(100, Math.round((elapsedDays / total) * 100));
              return (
                <div key={e.id} className="card-warm rounded-2xl overflow-hidden" data-testid={`my-saadhna-${e.id}`}>
                  <div className="flex">
                    {s.image_url && <img src={s.image_url} alt="" className="h-32 w-32 object-cover shrink-0" />}
                    <div className="p-4 flex-1">
                      <p className="text-xs uppercase tracking-widest text-[#B45309]">{s.deity}</p>
                      <p className="font-serif text-lg text-[#5C061C] leading-tight">{e.saadhna_title}</p>
                      {e.is_active ? (
                        <>
                          <div className="mt-2 flex items-center justify-between text-xs">
                            <span className="inline-flex items-center gap-1 text-[#5C061C] font-semibold"><CalendarCheck className="h-3 w-3" /> Day {elapsedDays} of {total}</span>
                            <span className="text-[#6E6259]">{remaining} left</span>
                          </div>
                          <Progress value={pct} className="h-1.5 mt-1.5 bg-[#F4EEDF]" />
                          <p className="text-[10px] text-[#6E6259] mt-1 italic">"Steady, gentle, everyday — that is saadhna."</p>
                        </>
                      ) : (
                        <div className="mt-2 text-xs text-[#DC2626] font-medium">Access expired</div>
                      )}
                      <Button onClick={() => setOpenSaadhna({ ...e, elapsedDays, total, remaining, pct })} size="sm" className="btn-saffron rounded-full h-8 mt-2" disabled={!e.is_active} data-testid={`open-saadhna-${e.id}`}>
                        <Flame className="h-3 w-3 mr-1" /> Open guidance
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {openSaadhna && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={() => setOpenSaadhna(null)}>
          <div className="bg-[#FAF7F2] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="saadhna-guidance-modal">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#B45309]">{openSaadhna.saadhna?.deity}</p>
                <h3 className="font-serif text-2xl text-[#5C061C]">{openSaadhna.saadhna_title}</h3>
                {openSaadhna.total && (
                  <div className="mt-2 max-w-xs">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="inline-flex items-center gap-1 text-[#5C061C] font-semibold"><CalendarCheck className="h-3 w-3" /> Day {openSaadhna.elapsedDays} of {openSaadhna.total}</span>
                      <span className="text-[#6E6259]">{openSaadhna.remaining} left</span>
                    </div>
                    <Progress value={openSaadhna.pct} className="h-1.5 bg-[#F4EEDF]" />
                  </div>
                )}
              </div>
              <Button variant="ghost" onClick={() => setOpenSaadhna(null)}>Close</Button>
            </div>
            {openSaadhna.saadhna?.mantra && (
              <div className="card-warm rounded-xl p-4 mb-3">
                <p className="text-xs uppercase tracking-widest text-[#B45309] mb-2">{t("saadhna.mantra")}</p>
                <p className="font-serif text-lg text-[#5C061C] leading-relaxed">{openSaadhna.saadhna.mantra}</p>
              </div>
            )}
            {openSaadhna.saadhna?.daily_routine && (
              <div className="card-warm rounded-xl p-4 mb-3">
                <p className="text-xs uppercase tracking-widest text-[#B45309] mb-2">{t("saadhna.routine")}</p>
                <p className="text-[#1F1A17] whitespace-pre-line leading-relaxed">{openSaadhna.saadhna.daily_routine}</p>
              </div>
            )}
            {openSaadhna.saadhna?.guidance_content && (
              <div className="card-warm rounded-xl p-4">
                <p className="text-xs uppercase tracking-widest text-[#B45309] mb-2">{t("saadhna.guidance")}</p>
                <p className="text-[#1F1A17] whitespace-pre-line leading-relaxed">{openSaadhna.saadhna.guidance_content}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-[#B45309]">{label}</Label>
      {children}
    </div>
  );
}
